import { outOfServiceAreaInput } from '@trafiku/contracts';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { fc, GeoJsonLayer, lineLayer } from '@/components/map/layers';
import { boundsOf, MapView } from '@/components/map/map-view';
import { OverviewMap } from '@/components/map/overview-map';
import { ShapeEditor } from '@/components/map/shape-editor';
import { ReadOnlyNotice } from '@/components/read-only-notice';
import { ResourceList } from '@/components/resource-list';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { useAuth } from '@/lib/auth';
import { useFormState } from '@/lib/form';
import { t } from '@/lib/i18n';
import { linesCollection, useNetwork } from '@/lib/network';
import { useItem, useSave } from '@/lib/resources';
import type { OutOfServiceArea } from '@/lib/types';

export function OutOfServiceAreasPage() {
  const { can } = useAuth();
  const network = useNetwork();
  return (
    <ResourceList<OutOfServiceArea>
      title={t('Out-of-service areas')}
      subtitle={t('Depots and garages: buses inside these areas are hidden from the app.')}
      map={({ hovered, hover }) => (
        <OverviewMap
          data={fc(
            (network.data?.out_of_service_areas ?? []).map((a) => ({
              type: 'Feature' as const,
              geometry: a.geometry,
              properties: { id: a._id, name: a.name, color: '#dc2626' },
            })),
          )}
          kind="polygon"
          hovered={hovered}
          onHover={hover}
          basePath="/out-of-service-areas"
        >
          <GeoJsonLayer
            data={linesCollection(network.data?.lines)}
            layers={[lineLayer(undefined, 3, 0.35)]}
          />
        </OverviewMap>
      )}
      resource="out-of-service-areas"
      basePath="/out-of-service-areas"
      newLabel={t('New area')}
      canCreate={can('admin')}
      columns={[
        { header: t('Name'), cell: (a) => <span className="font-medium">{a.name}</span> },
        { header: t('Description'), cell: (a) => a.description || '—' },
      ]}
    />
  );
}

export function OutOfServiceAreaEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<OutOfServiceArea>('out-of-service-areas', id);
  const network = useNetwork();
  const save = useSave<OutOfServiceArea>('out-of-service-areas', id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const readOnly = !can('admin');
  const form = useFormState({
    name: '',
    description: '',
    geometry: null as OutOfServiceArea['geometry'] | null,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    if (item.data)
      form.setValues({
        name: item.data.name,
        description: item.data.description ?? '',
        geometry: item.data.geometry,
      });
  }, [item.data]);
  if (!isNew && item.isLoading) return <Spinner />;
  const { values, set, errors } = form;
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(outOfServiceAreaInput, (d) => save.mutateAsync(d)))
          navigate('/out-of-service-areas');
      }}
    >
      <PageHeader title={isNew ? t('New out-of-service area') : values.name} back="/out-of-service-areas" />
      {readOnly && <ReadOnlyNotice />}
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <Card>
          <fieldset disabled={readOnly} className="min-w-0 space-y-4">
            <Field label={t('Name')} error={errors.name}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  invalid={!!errors.name}
                />
              )}
            </Field>
            <Field label={t('Description')}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('Area')} error={errors.geometry}>
              {() => (
                <p className="text-xs text-slate-600">
                  {values.geometry ? t('Drawn') : t('Draw the area on the map')}
                </p>
              )}
            </Field>
          </fieldset>
          <div className="mt-6">
            <FormActions
              saving={save.isPending}
              resource="out-of-service-areas"
              id={id}
              backTo="/out-of-service-areas"
              canDelete={can('admin')}
              canSave={can('admin')}
            />
          </div>
        </Card>
        <MapView className="h-[600px]" fit={boundsOf(values.geometry?.coordinates)}>
          <GeoJsonLayer data={linesCollection(network.data?.lines)} layers={[lineLayer(undefined, 3, 0.5)]} />
          <ShapeEditor
            readOnly={readOnly}
            slots={[
              { key: 'area', label: t('Area'), kind: 'Polygon', color: '#dc2626', value: values.geometry },
            ]}
            onChange={(_, g) => set('geometry', (g as OutOfServiceArea['geometry']) ?? null)}
          />
        </MapView>
      </div>
    </form>
  );
}
